import api from './api';
import { getMonthName } from './dashboardData';

import {
  Text,
  TextInput,
  Select,
  Table,
  Badge,
  Paper,
  Box,
  Flex,
  Group,
  Pagination,
  ActionIcon,
  Button,
} from '@mantine/core';

import {
  IconSearch,
  IconFilter,
  IconDownload,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';

import { useState, useMemo, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';

import * as XLSX from 'xlsx';

// ============================================================
// MESES
// ============================================================

const MESES = [
  { value: '1', label: 'ENERO' },
  { value: '2', label: 'FEBRERO' },
  { value: '3', label: 'MARZO' },
  { value: '4', label: 'ABRIL' },
  { value: '5', label: 'MAYO' },
  { value: '6', label: 'JUNIO' },
  { value: '7', label: 'JULIO' },
  { value: '8', label: 'AGOSTO' },
  { value: '9', label: 'SEPTIEMBRE' },
  { value: '10', label: 'OCTUBRE' },
  { value: '11', label: 'NOVIEMBRE' },
  { value: '12', label: 'DICIEMBRE' },
];

const DIAS = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
];

// ============================================================
// COLOR FOC
// ============================================================

function getCellColor(val) {
  if (
    val === '' ||
    val === undefined ||
    val === null
  ) {
    return {
      bg: undefined,
      c: 'dimmed',
    };
  }

  const num = parseInt(val);

  if (isNaN(num)) {
    return {
      bg: undefined,
      c: 'dimmed',
    };
  }

  if (num === 0) {
    return {
      bg: undefined,
      c: 'dimmed',
    };
  }

  if (num >= 70) {
    return {
      bg: 'rgba(250,0,115,0.18)',
      c: '#FA0073',
    };
  }

  if (num >= 50) {
    return {
      bg: 'rgba(250,0,115,0.08)',
      c: '#020C41',
    };
  }

  if (num >= 30) {
    return {
      bg: 'rgba(3,13,117,0.06)',
      c: '#020C41',
    };
  }

  return {
    bg: undefined,
    c: '#020C41',
  };
}

// ============================================================
// COMPONENT
// ============================================================

export default function Fco() {

  const { canDownloadReports } = usePermissions();
  
  // ============================================================
  // PERIODO
  // ============================================================

  const now = new Date();

  const [mes, setMes] = useState(
    now.getMonth() + 1
  );

  const [anio, setAnio] = useState(
    now.getFullYear()
  );

  const prevMonth = () => {
    if (mes === 1) {
      setMes(12);
      setAnio(anio - 1);
    } else {
      setMes(mes - 1);
    }

    setPage(1);
  };

  const nextMonth = () => {
    if (mes === 12) {
      setMes(1);
      setAnio(anio + 1);
    } else {
      setMes(mes + 1);
    }

    setPage(1);
  };

  // ============================================================
  // DATA
  // ============================================================

  const [focData, setFocData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setApiError(null);

      try {
        const res = await api.get(
          `/foc?mes=${mes}&anio=${anio}&limit=5000`
        );

        setFocData(
          res.data?.data || []
        );
      } catch (e) {
        console.warn(
          'API error:',
          e.message
        );

        setApiError(e.message);
        setFocData([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [mes, anio]);

  // ============================================================
  // FILTROS
  // ============================================================

  const [search, setSearch] = useState('');
  const [rutaFilter, setRutaFilter] =
    useState(null);

  const [page, setPage] = useState(1);

  const perPage = 25;

  // ============================================================
  // OPCIONES DE AÑO
  // ============================================================

  const yearOptions = useMemo(() => {
    const currentYear =
      new Date().getFullYear();

    return Array.from(
      {
        length: 7,
      },
      (_, i) => {
        const year =
          currentYear - 3 + i;

        return {
          value: String(year),
          label: String(year),
        };
      }
    );
  }, []);

  // ============================================================
  // CAMBIO DE MES
  // ============================================================

  const handleMonthChange = (value) => {
    if (!value) return;

    setMes(
      parseInt(value, 10)
    );

    setPage(1);
  };

  // ============================================================
  // CAMBIO DE AÑO
  // ============================================================

  const handleYearChange = (value) => {
    if (!value) return;

    setAnio(
      parseInt(value, 10)
    );

    setPage(1);
  };

  // ============================================================
  // OPCIONES DE RUTA
  // ============================================================

  const rutaOptions = useMemo(() => {
    const rutas = [
      ...new Set(
        focData
          .map((d) => d.Ruta)
          .filter(Boolean)
      ),
    ];

    return rutas
      .sort()
      .map((r) => ({
        value: r,
        label: r,
      }));
  }, [focData]);

  // ============================================================
  // FILTRADO
  // ============================================================

  const filtered = useMemo(() => {
    let data = [...focData];

    if (search) {
      const s =
        search.toLowerCase();

      data = data.filter((d) => {
        const id =
          String(d.ID || '')
            .toLowerCase();

        const ruta =
          String(d.Ruta || '')
            .toLowerCase();

        const vlo =
          String(d.VLO || '')
            .toLowerCase();

        return (
          id.includes(s) ||
          ruta.includes(s) ||
          vlo.includes(s)
        );
      });
    }

    if (rutaFilter) {
      data = data.filter(
        (d) =>
          d.Ruta === rutaFilter
      );
    }

    return data;
  }, [
    focData,
    search,
    rutaFilter,
  ]);

  // ============================================================
  // PAGINACIÓN
  // ============================================================

  const totalPages = Math.max(
    1,
    Math.ceil(
      filtered.length / perPage
    )
  );

  const paginatedData =
    filtered.slice(
      (page - 1) * perPage,
      page * perPage
    );

  // ============================================================
  // EXPORTAR EXCEL
  // ============================================================

  const exportToExcel = () => {
    const exportData =
      filtered.map((row) => {
        const obj = {
          ID: row.ID,
          Ruta: row.Ruta,
          VLO: row.VLO,
        };

        DIAS.forEach((dia) => {
          obj[dia] =
            row[dia] || '';
        });

        return obj;
      });

    const ws =
      XLSX.utils.json_to_sheet(
        exportData
      );

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      'FOC'
    );

    XLSX.writeFile(
      wb,
      `FOC_${anio}_${String(
        mes
      ).padStart(2, '0')}.xlsx`
    );
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      {/* ======================================================
          HEADER
          ====================================================== */}

      <Flex
        justify="space-between"
        align="center"
        mb="md"
        wrap="wrap"
        gap="md"
      >
        <Text
          fw={700}
          size="xl"
          c="#020C41"
        >
          FOC — Factor de Ocupación
        </Text>

        {/* ====================================================
            SELECTOR MES / AÑO
            ==================================================== */}

        <Group
          gap="xs"
          align="center"
        >
          {/* Mes anterior */}

          <ActionIcon
            size="sm"
            variant="light"
            color="#030D75"
            onClick={prevMonth}
            title="Mes anterior"
          >
            <IconChevronLeft
              size={14}
            />
          </ActionIcon>

          {/* Mes */}

          <Select
            value={String(mes)}
            onChange={
              handleMonthChange
            }
            data={MESES}
            size="xs"
            allowDeselect={false}
            style={{
              width: 135,
            }}
          />

          {/* Año */}

          <Select
            value={String(anio)}
            onChange={
              handleYearChange
            }
            data={yearOptions}
            size="xs"
            allowDeselect={false}
            style={{
              width: 90,
            }}
          />

          {/* Mes siguiente */}

          <ActionIcon
            size="sm"
            variant="light"
            color="#030D75"
            onClick={nextMonth}
            title="Mes siguiente"
          >
            <IconChevronRight
              size={14}
            />
          </ActionIcon>
        </Group>

        <Text
          size="xs"
          c="dimmed"
          fs="italic"
        >
          Última carga:{' '}
          {new Date().toLocaleString(
            'es-MX',
            {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }
          )}
        </Text>
      </Flex>

      {/* ======================================================
          FILTROS
          ====================================================== */}

      <Paper
        shadow="xs"
        p="md"
        mb="md"
        radius="md"
      >
        <Flex
          gap="md"
          align="end"
          wrap="wrap"
        >
          <TextInput
            label="Buscar"
            placeholder="ID, Ruta o Vuelo..."
            leftSection={
              <IconSearch size={16} />
            }
            value={search}
            onChange={(e) => {
              setSearch(
                e.currentTarget.value
              );
              setPage(1);
            }}
            style={{
              flex: 1,
              minWidth: 200,
            }}
          />

          <Select
            label="Filtrar por Ruta"
            placeholder="Todas las rutas"
            data={rutaOptions}
            value={rutaFilter}
            onChange={(val) => {
              setRutaFilter(val);
              setPage(1);
            }}
            clearable
            searchable
            leftSection={
              <IconFilter size={16} />
            }
            style={{
              minWidth: 200,
            }}
          />
          {
            canDownloadReports && (  
            <Button size="sm" variant="light" color="#FA0073" leftSection={  <IconDownload size={16} /> } onClick={exportToExcel} > Excel </Button>
            )
          }
        
        </Flex>
      </Paper>

      {/* ======================================================
          TABLA
          ====================================================== */}

      <Paper
        shadow="xs"
        radius="md"
        style={{
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            overflowX: 'auto',
          }}
        >
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="sm"
            verticalSpacing="xs"
            layout="auto"
            style={{
              textAlign: 'center',
            }}
          >
            <Table.Thead
              style={{
                backgroundColor:
                  '#030D75',
              }}
            >
              <Table.Tr>
                <Table.Th
                  style={{
                    color: 'white',
                    textAlign:
                      'center',
                    whiteSpace:
                      'nowrap',
                  }}
                >
                  ID
                </Table.Th>

                <Table.Th
                  style={{
                    color: 'white',
                    textAlign:
                      'center',
                  }}
                >
                  Ruta
                </Table.Th>

                <Table.Th
                  style={{
                    color: 'white',
                    textAlign:
                      'center',
                  }}
                >
                  VLO
                </Table.Th>

                {DIAS.map(
                  (d) => (
                    <Table.Th
                      key={d}
                      style={{
                        color:
                          'white',
                        textAlign:
                          'center',
                        whiteSpace:
                          'nowrap',
                      }}
                    >
                      {d}
                    </Table.Th>
                  )
                )}
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={
                      3 +
                      DIAS.length
                    }
                    style={{
                      textAlign:
                        'center',
                      padding: 30,
                    }}
                  >
                    <Text
                      size="sm"
                      c="dimmed"
                    >
                      Cargando
                      información...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : paginatedData.length ===
                0 ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={
                      3 +
                      DIAS.length
                    }
                    style={{
                      textAlign:
                        'center',
                      padding: 30,
                    }}
                  >
                    <Text
                      size="sm"
                      c="dimmed"
                    >
                      No hay
                      información
                      para{' '}
                      {getMonthName(
                        mes
                      ).toUpperCase()}{' '}
                      {anio}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginatedData.map(
                  (row) => (
                    <Table.Tr
                      key={
                        row.ID +
                        row.VLO
                      }
                    >
                      <Table.Td
                        style={{
                          textAlign:
                            'center',
                        }}
                      >
                        <Text
                          size="sm"
                          fw={500}
                          c="#020C41"
                        >
                          {row.ID}
                        </Text>
                      </Table.Td>

                      <Table.Td
                        style={{
                          textAlign:
                            'center',
                        }}
                      >
                        <Badge
                          variant="light"
                          color={
                            String(
                              row.Ruta ||
                                ''
                            ).startsWith(
                              'MEX'
                            )
                              ? 'am-blue'
                              : 'am-pink'
                          }
                          size="sm"
                        >
                          {row.Ruta}
                        </Badge>
                      </Table.Td>

                      <Table.Td
                        style={{
                          textAlign:
                            'center',
                        }}
                      >
                        <Text size="sm">
                          {row.VLO}
                        </Text>
                      </Table.Td>

                      {DIAS.map(
                        (dia) => {
                          const val =
                            row[dia];

                          const {
                            bg,
                            c,
                          } =
                            getCellColor(
                              val
                            );

                          return (
                            <Table.Td
                              key={dia}
                              style={{
                                textAlign:
                                  'center',
                                backgroundColor:
                                  bg,
                              }}
                            >
                              <Text
                                size="sm"
                                fw={
                                  parseInt(
                                    val
                                  ) >=
                                  50
                                    ? 600
                                    : 400
                                }
                                c={c}
                              >
                                {val ||
                                  '—'}
                              </Text>
                            </Table.Td>
                          );
                        }
                      )}
                    </Table.Tr>
                  )
                )
              )}
            </Table.Tbody>
          </Table>
        </Box>

        {/* ====================================================
            PAGINACIÓN
            ==================================================== */}

        <Flex
          justify="space-between"
          align="center"
          p="md"
        >
          <Text
            size="sm"
            c="dimmed"
          >
            {filtered.length ===
            0
              ? '0 registros'
              : `Mostrando ${
                  (page - 1) *
                    perPage +
                  1
                }–${Math.min(
                  page * perPage,
                  filtered.length
                )} de ${
                  filtered.length
                }`}
          </Text>

          <Pagination
            total={totalPages}
            value={page}
            onChange={setPage}
            color="am-blue"
            size="sm"
          />
        </Flex>
      </Paper>
    </>
  );
}